import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { feedStockApi, CostAnalysis, UsageTrend, TrendAnalytics } from '../api/feedStock';

interface UseFeedStockAnalyticsReturn {
  // Cost Analysis
  costAnalysis: CostAnalysis | null;
  costAnalysisLoading: boolean;
  costAnalysisError: string | null;
  
  // Usage Trends
  usageTrends: UsageTrend | null;
  usageTrendsLoading: boolean;
  usageTrendsError: string | null;
  
  // Trend Analytics
  trendAnalytics: TrendAnalytics | null;
  trendAnalyticsLoading: boolean;
  trendAnalyticsError: string | null;
  
  // Actions
  fetchCostAnalysis: (startDate?: string, endDate?: string) => Promise<void>;
  fetchUsageTrends: (startDate?: string, endDate?: string) => Promise<void>;
  fetchTrendAnalytics: (startDate?: string, endDate?: string) => Promise<void>;
  refreshAll: (startDate?: string, endDate?: string) => Promise<void>;
}

export const useFeedStockAnalytics = (): UseFeedStockAnalyticsReturn => {
  // Cost Analysis State
  const [costAnalysis, setCostAnalysis] = useState<CostAnalysis | null>(null);
  const [costAnalysisLoading, setCostAnalysisLoading] = useState(false);
  const [costAnalysisError, setCostAnalysisError] = useState<string | null>(null);
  
  // Usage Trends State
  const [usageTrends, setUsageTrends] = useState<UsageTrend | null>(null);
  const [usageTrendsLoading, setUsageTrendsLoading] = useState(false);
  const [usageTrendsError, setUsageTrendsError] = useState<string | null>(null);
  
  // Trend Analytics State
  const [trendAnalytics, setTrendAnalytics] = useState<TrendAnalytics | null>(null);
  const [trendAnalyticsLoading, setTrendAnalyticsLoading] = useState(false);
  const [trendAnalyticsError, setTrendAnalyticsError] = useState<string | null>(null);

  // Fetch Cost Analysis
  const fetchCostAnalysis = useCallback(async (startDate?: string, endDate?: string) => {
    try {
      setCostAnalysisLoading(true);
      setCostAnalysisError(null);
      
      const response = await feedStockApi.getCostAnalysis(startDate, endDate);
      setCostAnalysis(response.data);
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to fetch cost analysis';
      setCostAnalysisError(errorMessage);
    } finally {
      setCostAnalysisLoading(false);
    }
  }, []);

  // Fetch Usage Trends
  const fetchUsageTrends = useCallback(async (startDate?: string, endDate?: string) => {
    try {
      setUsageTrendsLoading(true);
      setUsageTrendsError(null);
      
      const response = await feedStockApi.getUsageTrends(startDate, endDate);
      setUsageTrends(response.data);
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to fetch usage trends';
      setUsageTrendsError(errorMessage);
    } finally {
      setUsageTrendsLoading(false);
    }
  }, []);

  // Fetch Trend Analytics
  const fetchTrendAnalytics = useCallback(async (startDate?: string, endDate?: string) => {
    try {
      setTrendAnalyticsLoading(true);
      setTrendAnalyticsError(null);
      
      const response = await feedStockApi.getTrendAnalytics(startDate, endDate);
      setTrendAnalytics(response.data);
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to fetch trend analytics';
      setTrendAnalyticsError(errorMessage);
    } finally {
      setTrendAnalyticsLoading(false);
    }
  }, []);

  // Refresh all analytics
  const refreshAll = useCallback(async (startDate?: string, endDate?: string) => {
    await Promise.all([
      fetchCostAnalysis(startDate, endDate),
      fetchUsageTrends(startDate, endDate),
      fetchTrendAnalytics(startDate, endDate)
    ]);
  }, [fetchCostAnalysis, fetchUsageTrends, fetchTrendAnalytics]);

  // Load initial data
  useEffect(() => {
    refreshAll();
  }, []);

  return {
    // Cost Analysis
    costAnalysis,
    costAnalysisLoading,
    costAnalysisError,
    
    // Usage Trends
    usageTrends,
    usageTrendsLoading,
    usageTrendsError,
    
    // Trend Analytics
    trendAnalytics,
    trendAnalyticsLoading,
    trendAnalyticsError,
    
    // Actions
    fetchCostAnalysis,
    fetchUsageTrends,
    fetchTrendAnalytics,
    refreshAll
  };
}; 